import { Button, Card, CardContent, Grid, Paper } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { experimentsService } from '../../Services/Experiments.services';
import { Header } from '../../Components/Shared/Header/Header';
import { useEffect, useState } from 'react';
import SkeletonComponent from '../../Components/SkeletonComponent';
import RunsTable from '../../Components/BodyComponent/Experiments/RunsTable';
import { FieldKeys } from '../../Components/BodyComponent/Experiments/RunForm';
import ExperimentTitle from '../../Components/BodyComponent/Experiments/ExperimentTitle';
import ExperimentBreadcrumbs from '../../Components/BodyComponent/Experiments/ExperimentBreadcrumbs';

function ExperimentRunsPage() {
    const { experimentId } = useParams();
    const navigate = useNavigate();
    const [isRunsLoading, setIsRunsLoading] = useState(true);
    const [runs, setRuns] = useState([]);

    const fetchRuns = async () => {
        setIsRunsLoading(true);
        try {
            const runs = await experimentsService.getExperimentRuns(experimentId);
            setRuns(runs);
        } catch (err) {
            console.error('Error fetching runs:', err);
            setRuns([]);
        } finally {
            setIsRunsLoading(false);
        }
    };

    useEffect(() => {
        fetchRuns();
    }, [experimentId]);

    const handleGoToEvaluation = () => {
        navigate(`/experiments/${experimentId}/evaluation`);
    };

    const handleRunDelete = async (documentId) => {
        try {
            await experimentsService.deleteExperimentRun(experimentId, documentId);
            setRuns((runs) => runs.filter((doc) => doc.id !== documentId));
        } catch (err) {
            console.error('Error deleting the run:', err);
        } finally {
            setIsRunsLoading(false);
        }
    };

    const handleAddRun = async () => {
        try {
            const newRun = await experimentsService.createExperimentRun(experimentId);

            if (newRun?.id) {
                navigate(`/experiments/${experimentId}/runs/${newRun.id}`);
            }
        } catch (error) {
            console.error('Error creating experiment:', error);
        }
    };

    const handleViewDocs = () => {
        navigate(`/experiments/${experimentId}/docs`);
    };

    const handleEditRun = async (run) => {
        navigate(`/experiments/${experimentId}/runs/${run.id}`, {
            state: {
                [FieldKeys.name]: run[FieldKeys.name] ?? null,
                [FieldKeys.isGoldStandard]: run[FieldKeys.isGoldStandard] ?? false,
            },
        });
    };

    return (
        <Grid item xs={12} sm={12} md={12} sx={{ mt: 2, mb: 2 }}>
            <Card component={Paper}>
                <CardContent>
                    <div className="header">
                        <Header
                            title={(
                                <ExperimentBreadcrumbs
                                    crumbs={[
                                        {
                                            label: 'Experiments',
                                            href: '/experiments',
                                        },
                                        {
                                            label: (<ExperimentTitle experimentId={experimentId} />),
                                        },
                                    ]}
                                    pageTitle="Runs"
                                />
                            )}
                            actionButtons={[
                                (
                                    <Button
                                        key="add-run-button"
                                        variant="outlined"
                                        sx={{ ml: 2 }}
                                        onClick={handleAddRun}
                                    >
                                        Add Run
                                    </Button>
                                ),
                                (
                                    <Button
                                        key="go-to-docs-button"
                                        variant="outlined"
                                        onClick={handleViewDocs}
                                    >
                                        Docs
                                    </Button>
                                ),
                                (
                                    <Button
                                        key="go-to-evaluation-button"
                                        variant="contained"
                                        sx={{ ml: 2 }}
                                        onClick={handleGoToEvaluation}
                                    >
                                        Evaluate
                                    </Button>
                                ),
                            ]}
                        />
                    </div>
                    <div>
                        {isRunsLoading
                            ? <SkeletonComponent />
                            : (
                                <RunsTable
                                    experimentId={experimentId}
                                    runs={runs}
                                    onDocumentDelete={handleRunDelete}
                                    onDocumentEdit={handleEditRun}
                                />
                            )}
                    </div>
                </CardContent>
            </Card>
        </Grid>
    );
}

export default ExperimentRunsPage;
